import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import VoiceRecorder from './VoiceRecorder';
import AudioPlayer from './AudioPlayer';
import Waveform from './Waveform';
import ProcessingBar from './ProcessingBar';

const getInstructionText = (appState) => {
  switch (appState) {
    case 'idle':
      return 'Tap the button and speak';
    case 'listening':
      return 'I’m Listening … Tap again to cancel';
    case 'processing':
      return 'Preparing the reply, one moment…';
    case 'replying':
      return 'Playing the reply… Tap to stop';
    default:
      return '';
  }
};

const AppContainer = styled.div`
  background: linear-gradient(to top, #000A12 0%, #020816 100%);
  color: #fff;
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 0;
  font-family: 'Arial', sans-serif;
  overflow: hidden;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  margin-bottom: 10px;
  font-weight: 700;
  letter-spacing: 2px;
  background: linear-gradient(90deg, #00B2FF 0%, #0CC3CD 25%, #00BCD4 50%, #0CC3CD 75%, #00B2FF 100%);
  background-size: 400% auto;
  background-position: center;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-top: -20px;
`;

const InstructionText = styled.p`
  font-size: 1rem;
  margin: 20px 0;
  opacity: 0.8;
  min-height: 24px;
`;

const MicButton = styled.button`
  appearance: none;
  -webkit-appearance: none;
  background-clip: padding-box;
  backface-visibility: hidden;
  will-change: background;
  background: ${({ $active, $replying }) => {
    if ($active) {
      return 'linear-gradient(90deg, #00B2FF 0%, #0CC3CD 25%, #00BCD4 50%, #0CC3CD 75%, #00B2FF 100%)';
    } else if ($replying) {
      // Aitox Cyan for replying state (clean, brand-aligned)
      return 'linear-gradient(90deg, #00BCD4 0%, #00E6F6 50%, #00BCD4 100%)';
    } else {
      return 'radial-gradient(circle at center, #0A1B2A 0%, #020816 100%)';
    }
  }};
  border: none;
  border-radius: 50%;
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  box-shadow: inset 0 0 0 8px rgba(255, 255, 255, 0.04),
              0 0 0 1px rgba(255, 255, 255, 0.1);
  transition: background 0.3s ease;
  outline: none;
  backdrop-filter: none;
  -webkit-tap-highlight-color: transparent;

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.06) 40%, transparent 70%);
    z-index: 0;
  }

  svg {
    position: relative;
    z-index: 1;
    width: 64px;
    height: 64px;
    fill: #fff;
  }
`;

const WaveContainer = styled.div`
  position: absolute;
  top: calc(50% + 140px);
  width: 100%;
  display: flex;
  justify-content: center;
  background: transparent;
`;

function App() {
  const [voiceUrl, setVoiceUrl] = useState(null);
  const [appState, setAppState] = useState('idle'); // 'idle' | 'listening' | 'processing' | 'replying'

  const audioRef = useRef(null);

  const handleResponse = (url) => {
    setVoiceUrl(url);
    setAppState('replying');
  };

  const [userInstruction, setUserInstruction] = useState(getInstructionText(appState));

  useEffect(() => {
    const instruction = getInstructionText(appState);
    setUserInstruction(instruction);
  }, [appState]);

  return (
    <AppContainer>
      <Title>AITOXA Pro</Title>
      <InstructionText>{userInstruction}</InstructionText>
      <MicButton
        onClick={() => {
          if (appState === 'listening') {
            if (window.stopRecording) window.stopRecording();
            setAppState('idle');
          } else if (appState === 'replying') {
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0; // reset to beginning
            }
            setVoiceUrl(null);
            setAppState('idle');
          } else if (appState === 'idle') {
            if (window.startRecording) {
              setAppState('listening');
              window.startRecording();
            }
          }
        }}
        $active={appState === 'listening'}
        $replying={appState === 'replying'}
      >
        <svg viewBox="0 0 24 24" width="60" height="60" xmlns="http://www.w3.org/2000/svg" fill="white">
          <path d="M12 14c1.93 0 3.5-1.57 3.5-3.5v-5c0-1.93-1.57-3.5-3.5-3.5S8.5 3.57 8.5 5.5v5c0 1.93 1.57 3.5 3.5 3.5z"/>
          <path d="M17.5 10.5v1c0 3.03-2.47 5.5-5.5 5.5s-5.5-2.47-5.5-5.5v-1" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
          <line x1="12" y1="17" x2="12" y2="21" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <line x1="9" y1="21" x2="15" y2="21" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </MicButton>
      {(appState === 'listening' || appState === 'replying') && (
        <WaveContainer>
          <Waveform />
        </WaveContainer>
      )}
      {appState === 'processing' && <ProcessingBar />}
      <VoiceRecorder onResponse={handleResponse} setAppState={setAppState} />
      <AudioPlayer
        voiceUrl={voiceUrl}
        audioRef={audioRef}
        appState={appState}
        showManualPlay={true}
        onEnded={() => {
          setAppState('idle');
          setVoiceUrl(null);
        }}
      />
    </AppContainer>
  );
}

export default App;
